const _excluded = ["props"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
// @ts-strict-ignore
import React, { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { DisplayContext } from 'common/widgets/date-range-picker/models/types';
import { DATE_RANGE_TYPES } from 'common/widgets/date-range-picker/models/date-range-factory';
import { TooltipReact } from 'common/components/tooltip';
import { Placement } from 'common/components/tooltip/types';
import { queryBuilderUtils } from 'common/query-builder/query-builder/util';
import { getDateRangeTooltipRenderer } from 'common/widgets/date-range-preset-bar/date-range-tooltip';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { getCurrentMoment } from 'common/util/date';
import { DatePickerMode, getDateRangeLabel } from 'common/widgets/date-range-picker-new/util';
import { DateRange, DateRangeAttributes } from 'common/widgets/date-range-picker/models/date-range';
import { useDomEvent } from 'common/hooks/useDomEvent';
import { DateRangePickerReact } from 'common/widgets/date-range-picker-new';
import { TrackingUsageContext } from 'common/report/tracking-setup';
import { isShortWindow } from 'common/query-builder/query-block/utils/util';
import { DateRangePickerApplyEvent } from 'common/widgets/date-range-picker-new/types';
import { emitTrackEvent } from 'common/util/dom';
import { NewQueryBlock } from '../new-query-block';
import { SelectorType } from '../constants';
type Props = {
  datePickerModesOption?: DatePickerMode[];
  displayContext?: DisplayContext;
  maxDataHistoryDays?: number;
  projectAsOfDate?: string | null;
  singlePickerMode?: boolean;
  selected?: Partial<DateRangeAttributes>;
  trackName?: string;
  onSelect: (selection: DateRange) => void;
  onTrack?: (ev: any) => void;
} & Pick<React.ComponentProps<typeof NewQueryBlock>, `className` | `elref` | `buttonSize` | `selectorType` | `leftIcon` | `theme` | `readonly`>;
export function TimeQueryBlock(_ref: Props) {
  let {
    displayContext = DisplayContext.Default,
    selected,
    datePickerModesOption,
    projectAsOfDate,
    singlePickerMode = false,
    maxDataHistoryDays,
    trackName,
    onSelect,
    className,
    elref,
    buttonSize,
    selectorType = SelectorType.Text,
    leftIcon,
    theme,
    readonly,
    onTrack
  }: Props = _ref;
  const ref = useRef<HTMLDivElement>(null);
  useDomEvent(ref.current, "track", onTrack);
  const currentProject = useCurrentProject();
  const [open, setOpen] = useState(false);
  const [isTimePickerEnabled, setTimePickerEnabled] = useState(false);
  const dateRangeClass = DATE_RANGE_TYPES[displayContext];
  const selectedInstance = useMemo(() => selected ? new dateRangeClass(selected) : null, [selected, dateRangeClass]);
  const matchingPreset = useMemo(() => {
    if (!selectedInstance) {
      return null;
    }
    return dateRangeClass.getPresetOptions().find(option => {
      return selectedInstance.equals(new DateRange(option.value));
    });
  }, [selectedInstance, dateRangeClass]);
  const label = useMemo(() => getDateRangeLabel({
    dateRange: selectedInstance,
    matchingPreset,
    projectAsOfDate: null /* project as of date; we set this to "null" because we're trialing a change where we show the project-as-of-date in the tooltip, not the label */,
    now: getCurrentMoment({
      localized: true
    }),
    formatMode: selectorType === SelectorType.Text ? "none" : "simple"
  }), [selectedInstance, matchingPreset, selectorType]);
  const trackSelection = function trackSelection(_ref2) {
    let {
        props
      } = _ref2,
      eventData = _objectWithoutProperties(_ref2, _excluded);
    let cancelable = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    const additionalProps = {
      ["usage context"]: TrackingUsageContext.Report
    };
    if (trackName) {
      additionalProps["tab"] = trackName;
    }
    emitTrackEvent(ref.current, _objectSpread(_objectSpread({}, eventData), {}, {
      props: _objectSpread(_objectSpread({}, props), additionalProps),
      cancelable
    }));
  };
  const onDateApply = (detail: DateRangePickerApplyEvent[`detail`]) => {
    setTimePickerEnabled(detail.isTimePickerEnabled);
    onSelect(detail.dateRange);
    setOpen(false);
    trackSelection({
      eventName: "Select date range",
      props: {
        'date range from': detail.dateRange.from || null,
        'date range to': detail.dateRange.to || null,
        'date range type': detail.dateRange.type,
        'window unit': detail.dateRange.window ? detail.dateRange.window.unit : null,
        'window value': detail.dateRange.window ? detail.dateRange.window.value : null
      }
    });
  };
  const dateRangeAttributes = selectedInstance === null || selectedInstance === void 0 ? void 0 : selectedInstance.getAttributes();
  return <NewQueryBlock ref={ref} label={label} buttonSize={buttonSize} className={clsx("type-time", className)} elref={elref} menuHasCustomWidth open={open} onChangeOpen={setOpen} selectorTooltipElement={selectedInstance && <TooltipReact elref='dateRangeTooltip' placement={queryBuilderUtils.isCoreReport() ? Placement.Bottom : Placement.Top} lazyContentRenderer={getDateRangeTooltipRenderer(selectedInstance, currentProject.get().value)} />} readonly={readonly} selectorType={selectorType} theme={theme} leftIcon={leftIcon} menuElement={open && <DateRangePickerReact elref={"dateRangePicker"} datePickerModesOptions={datePickerModesOption} displayContext={DisplayContext[displayContext]} dateRange={dateRangeAttributes} isTimePickerEnabled={isTimePickerEnabled} projectAsOfDate={projectAsOfDate} singlePickerMode={singlePickerMode} maxHistoryDays={maxDataHistoryDays} trackedUseCase={TrackingUsageContext.QueryBuilder} forceHidePresetMenu onApply={ev => onDateApply(ev.detail)} onCancel={() => setOpen(false)} reduceHeight={isShortWindow()} />} data-sentry-element="NewQueryBlock" data-sentry-component="TimeQueryBlock" data-sentry-source-file="time-query-block.tsx" />;
}